import React, { useEffect, useState, useRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Plus, ChevronsRight, AlertCircle, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

import { taskAPI, innovatorAPI, projectAPI } from "../../services/api";
import { useWallet } from "../../context/WalletContext";
import { TaskFormModal } from "./task-components/TaskFormModal";
import { TaskCard } from "./task-components/TaskCard";
import { TaskDetailModal } from "./task-components/TaskDetailModal";

import { useLocation } from "react-router-dom";

export const STATUS_STYLES = {
  backlog: { bg: "bg-slate-400", dot: "bg-slate-200" },
  open: { bg: "bg-sky-400", dot: "bg-sky-200" },
  in_progress: { bg: "bg-violet-400", dot: "bg-violet-200" },
  submitted: { bg: "bg-amber-400", dot: "bg-amber-200" },
  approved: { bg: "bg-teal-400", dot: "bg-teal-200" },
  rejected: { bg: "bg-rose-400", dot: "bg-rose-200" },
};

/* ────────────────────────────────────────────────────────────────────────── */
/*  Helper wrapper: make the entire column a valid drop‑zone                */
/* ────────────────────────────────────────────────────────────────────────── */
function ColumnContainer({ id, children }) {
  const { setNodeRef } = useDroppable({
    id,
    data: { columnId: id },
  });

  return (
    <div
      ref={setNodeRef}
      className="flex w-72 min-w-72 flex-col gap-4 rounded-xl bg-gray-50 p-3 border border-gray-100 shadow-sm mx-1"
    >
      {children}
    </div>
  );
}

// Define columns outside of component to avoid re-creation
const COLUMNS = [
  { id: "backlog", title: "Backlog" },
  { id: "open", title: "Open" },
  { id: "in_progress", title: "In Progress" },
  { id: "submitted", title: "Submitted" },
  { id: "approved", title: "Approved" },
  { id: "rejected", title: "Rejected" },
];

export default function KanbanBoard({ projectId }) {
  const { walletAddress, isConnected, chainId } = useWallet();
  // Ensure walletAddress is never null/undefined for component operations
  const safeWalletAddress = walletAddress || "";
  const location = useLocation();
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const tokenIdParam = React.useMemo(() => {
    if (projectId) return projectId;

    const params = new URLSearchParams(location.search);
    return params.get("id");
  }, [location.search, projectId]);

  /* project fetched from backend */
  const [project, setProject] = useState(null);
  const [loadingProject, setLoadingProject] = useState(true);
  const [projectError, setProjectError] = useState(null);

  const [innovators, setInnovators] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null);
  const [showAppForm, setShowAppForm] = useState(false);
  const [showApplications, setShowApplications] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [showSubmissions, setShowSubmissions] = useState(false);

  const [appsOpen, setAppsOpen] = useState(false);
  const [subsOpen, setSubsOpen] = useState(false);
  const [appsModal, setAppsModal] = useState(false);
  const [subsModal, setSubsModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const isCreator = project?.creators?.some(
    (c) => c.walletAddress?.toLowerCase() === safeWalletAddress.toLowerCase()
  );

  // Show a scroll hint for new users
  useEffect(() => {
    if (tasks.length > 0 && !hasScrolled) {
      // Only show the hint if there's enough content to scroll
      const checkScrollable = () => {
        if (!scrollContainerRef.current) return;
        const { scrollWidth, clientWidth } = scrollContainerRef.current;
        if (scrollWidth > clientWidth) {
          setShowScrollHint(true);
          
          // Auto-hide the hint after 5 seconds
          const timer = setTimeout(() => {
            setShowScrollHint(false);
          }, 5000);
          
          return () => clearTimeout(timer);
        }
      };
      
      // Check after render and after tasks load
      setTimeout(checkScrollable, 100);
    }
  }, [tasks, hasScrolled]);

  // Scroll handlers for the arrows
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    // Show left arrow if scrolled right
    setShowLeftArrow(scrollLeft > 20);
    // Show right arrow if not scrolled all the way to the right
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 20);
    
    // User has manually scrolled
    if (!hasScrolled && scrollLeft > 0) {
      setHasScrolled(true);
      setShowScrollHint(false);
    }
  };

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
      
      // Also check if right arrow should be shown based on content width
      const checkOverflow = () => {
        if (scrollContainer.scrollWidth > scrollContainer.clientWidth) {
          setShowRightArrow(true);
        } else {
          setShowRightArrow(false);
        }
      };
      
      // Use ResizeObserver to detect changes in content size
      const resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(scrollContainer);
      
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [tasks]); // Re-run when tasks change as it affects content width

  useEffect(() => {
    if (!tokenIdParam) return;
    (async () => {
      try {
        setLoadingProject(true);
        const { data } = await projectAPI.getByIdPublic(tokenIdParam);
        setProject(data);
      } catch (err) {
        setProjectError(err.message || "Failed to load project");
      } finally {
        setLoadingProject(false);
      }
    })();
  }, [tokenIdParam]);

  /* ───────── fetch innovators once ───────── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await innovatorAPI.getAll();
        setInnovators(data);
      } catch (error) {
        console.error("Error fetching innovators:", error);
        // Continue even if innovators can't be fetched
        setInnovators([]);
      }
    })();
  }, []);

  /* ───────── fetch tasks ───────── */
  const fetchTasks = async (projId) => {
    if (!projId) return;
    try {
      const { data } = await taskAPI.getByProject(projId);
      setTasks(data);

      // Update detailTask if it exists in the fetched tasks
      setDetailTask((currentDetailTask) => {
        if (!currentDetailTask) return null;
        const updatedTask = data.find(
          (task) => task._id === currentDetailTask._id
        );
        return updatedTask || null;
      });
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setTasks([]);
    }
  };
  useEffect(() => {
    if (project?._id) fetchTasks(project._id);
  }, [project?._id]);

  /* ───────── helpers ───────── */
  const mergeTask = (t) => {
    setTasks((prev) => {
      const idx = prev.findIndex((x) => x._id === t._id);
      if (idx === -1) return [t, ...prev];
      const cp = [...prev];
      cp[idx] = t;
      return cp;
    });
    // if the detail modal is open for this task, refresh it too
    setDetailTask((curr) => (curr && curr._id === t._id ? t : curr));
    project && fetchTasks(project._id);
  };

  /* ───────── grouped tasks for columns ───────── */
  const tasksByStatus = COLUMNS.reduce((acc, c) => {
    acc[c.id] = tasks.filter((t) => t.status === c.id);
    return acc;
  }, {});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }, // must move 8px before drag starts
    })
  );

  const handleDragStart = ({ active }) => {
    // Only allow creators to drag tasks
    if (!isCreator) return;
    setActiveId(active.id);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveId(null);
    // Return early if not a creator or if no over target
    if (!isCreator || !over) return;
    
    const from = active.data.current.columnId;
    const to = over.data?.current?.columnId ?? over.id;
    
    // Find the task being dragged
    const draggedTask = tasks.find(t => t._id === active.id);
    if (!draggedTask) {
      console.error("Couldn't find the task being dragged");
      return;
    }
    
    // Special handling for "multiple" type tasks
    // Don't change status from submitted unless it's going to approved/rejected
    if (draggedTask.permissionType === "multiple" && 
        from === "submitted" && 
        !["approved", "rejected"].includes(to)) {
      // Prevent status change for multiple type tasks in submitted state
      // unless they're being approved/rejected
      fetchTasks(project._id);
      return;
    }
    
    const { data } = await taskAPI.update(active.id, { status: to });
    mergeTask(data);
  };

  /* ───────── helper: who may edit? ───────── */
  const canEditTask = (t) =>
    isConnected && isCreator && ["backlog", "open"].includes(t.status);

  const onRespond = async (appId, status, feedback) => {
    await taskAPI.respondToApplication(appId, { status, feedback });
    fetchTasks(project._id);
  };

  const onReleaseIUs = async (taskId, contributorId) => {
    // await taskAPI.releaseIUs(taskId, contributorId);
    fetchTasks(project._id);
  };

  // Helper function to generate etherscan links based on the current chain
  const getExplorerUrl = (txHash) => {
    // Default to Optimism Sepolia if chainId is not available
    const network = !chainId ? 'sepolia-optimism' :
      chainId === 10 ? 'optimistic' : 
      chainId === 11155420 ? 'sepolia-optimism' : 
      'sepolia-optimism';
    
    return `https://${network}.etherscan.io/tx/${txHash}`;
  };

  /* ───────── render ───────── */
  return (
    <>
      {loadingProject ? (
        <div className="flex h-[80vh] items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-gray-500">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            <p>Loading project tasks...</p>
          </div>
        </div>
      ) : projectError ? (
        <div className="flex h-[80vh] items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-gray-500 max-w-md text-center">
            <AlertCircle className="h-8 w-8 text-amber-500" />
            <p className="text-lg font-medium">Error loading tasks</p>
            <p className="text-sm text-gray-500">{projectError}</p>
          </div>
        </div>
      ) : tasks.length === 0 ? (
        <div className="flex h-[80vh] items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-gray-500 max-w-md text-center p-6 bg-gray-50 rounded-xl border border-gray-200">
            <div className="bg-indigo-100 p-3 rounded-full">
              <Plus className="h-6 w-6 text-indigo-600" />
            </div>
            <p className="text-lg font-medium text-gray-800">No tasks yet</p>
            <p className="text-sm text-gray-500">
              {isCreator 
                ? "Click the 'Add Task' button in the Backlog column to create your first task."
                : "This project doesn't have any tasks yet. Check back later!"}
            </p>
            {isConnected && isCreator && (
              <button
                onClick={() => {
                  setFormInitial(null);
                  setFormOpen(true);
                }}
                className="mt-2 inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4" /> Create First Task
              </button>
            )}
            {!isConnected && (
              <div className="mt-2 text-amber-700 bg-amber-50 p-3 rounded-lg text-sm">
                <p>Connect your wallet to create and interact with tasks.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="relative overflow-y-auto h-[80vh]">
          {/* Notice for non-creators */}
          {!isCreator && tasks.length > 0 && (
            <div className="mb-4 bg-amber-50 text-amber-800 py-2 px-4 text-sm border border-amber-200 rounded-lg flex items-center">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 text-amber-600" />
              <p>Only project creators can move tasks between columns</p>
            </div>
          )}

          {/* Scroll hint overlay */}
          {showScrollHint && (
            <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
              <div className="absolute right-12 top-1/2 -translate-y-1/2 transform animate-pulse bg-white/90 rounded-lg shadow-lg py-2 px-4 max-w-[200px] text-center">
                <div className="text-sm font-medium text-gray-800">Scroll to see more</div>
                <div className="text-xs text-gray-500 mt-1">Swipe horizontally or use arrow buttons</div>
                <div className="flex justify-center mt-2">
                  <ChevronLeft className="h-4 w-4 text-gray-400" />
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
          )}
          
          {/* Left scroll arrow - enhanced for mobile */}
          {showLeftArrow && (
            <button 
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-r-lg p-2 text-gray-600 hover:text-gray-900 border border-gray-200 border-l-0 transition-all duration-200 touch-manipulation"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-6 w-6" />
              <div className="absolute left-full top-1/2 -translate-y-1/2 h-12 w-6 bg-gradient-to-r from-white/80 to-transparent pointer-events-none" />
            </button>
          )}
          
          {/* Right scroll arrow - enhanced for mobile */}
          {showRightArrow && (
            <button 
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-l-lg p-2 text-gray-600 hover:text-gray-900 border border-gray-200 border-r-0 transition-all duration-200 touch-manipulation"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-6 w-6" />
              <div className="absolute right-full top-1/2 -translate-y-1/2 h-12 w-6 bg-gradient-to-l from-white/80 to-transparent pointer-events-none" />
            </button>
          )}
          
          {/* Mobile scroll indicators at bottom */}
          <div className="absolute bottom-1 left-0 right-0 flex justify-center items-center z-10 sm:hidden">
            <div className="bg-white/80 rounded-full px-3 py-1 shadow-md border border-gray-200">
              <div className="flex items-center gap-1">
                <div className={`h-2 w-2 rounded-full ${showLeftArrow ? 'bg-indigo-500' : 'bg-gray-300'}`}></div>
                <div className="h-2 w-8 rounded-full bg-gray-300"></div>
                <div className={`h-2 w-2 rounded-full ${showRightArrow ? 'bg-indigo-500' : 'bg-gray-300'}`}></div>
              </div>
            </div>
          </div>
          
          {/* Kanban board with scroll container */}
          <div
            ref={scrollContainerRef}
            className="flex h-[80vh] gap-2 overflow-x-auto py-2 pb-4 px-3 scroll-smooth hide-scrollbar"
            style={{ overscrollBehavior: "contain" }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              {COLUMNS.map((col) => (
                <ColumnContainer id={col.id} key={col.id}>
                  <header className="mb-3 flex items-center justify-between px-1">
                    <span
                      className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-white ${
                        STATUS_STYLES[col.id].bg
                      }`}
                    >
                      {/* coloured dot */}
                      <span
                        className={`h-2 w-2 rounded-full ${
                          STATUS_STYLES[col.id].dot
                        }`}
                      />
                      <span>{col.title}</span>
                    </span>

                    <span className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-200 text-xs font-medium text-gray-700">
                      {tasksByStatus[col.id].length}
                    </span>
                  </header>

                  <SortableContext
                    items={tasksByStatus[col.id].map((t) => t._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="flex h-full flex-col gap-3 overflow-y-auto pr-1">
                      {tasksByStatus[col.id].map((t) => (
                        <TaskCard
                          key={t._id}
                          task={t}
                          columnId={col.id}
                          onClick={setDetailTask}
                          onEdit={(task) => {
                            setFormInitial(task);
                            setFormOpen(true);
                          }}
                          canEdit={canEditTask(t)}
                          dragHandle={isCreator}
                        />
                      ))}
                      {isConnected && isCreator && col.id === "backlog" && (
                        <button
                          onClick={() => {
                            setFormInitial(null);
                            setFormOpen(true);
                          }}
                          className="mt-1 flex items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 bg-white py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                        >
                          <Plus className="h-4 w-4" /> Add Task
                        </button>
                      )}
                    </div>
                  </SortableContext>
                </ColumnContainer>
              ))}

              {/* overlay keeps dragged card above scroll containers */}
              <DragOverlay dropAnimation={null}>
                {activeId ? (
                  <TaskCard
                    task={tasks.find((t) => t._id === activeId) || { title: "" }}
                    columnId={""}
                    canEdit={false}
                    dragHandle={false}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      )}

      {/* ───────── Detail modal ───────── */}
      <TaskDetailModal
        open={Boolean(detailTask)}
        onClose={() => {
          setDetailTask(null);
          setShowSubmissions(false);
          setShowApplications(false);
          setShowAppForm(false);
          setShowSubmitForm(false);
          setAppsOpen(false);
          setSubsOpen(false);
          setAppsModal(false);
          setSubsModal(false);
          setSelectedApplication(null);
          setSelectedSubmission(null);
        }}
        task={detailTask}
        project={project}
        walletAddress={safeWalletAddress}
        innovators={innovators}
        onTaskUpdated={mergeTask}
        onOpenApplication={() => setAppsModal(!appsModal)}
        onOpenSubmission={() => setShowSubmitForm(!showSubmitForm)}
        isCreator={isCreator}
        onRespond={onRespond}
        showSubmitForm={showSubmitForm}
        setShowSubmitForm={setShowSubmitForm}
        onReleaseIUs={onReleaseIUs}
        appsOpen={appsOpen}
        setAppsOpen={setAppsOpen}
        subsOpen={subsOpen}
        setSubsOpen={setSubsOpen}
        appsModal={appsModal}
        setAppsModal={setAppsModal}
        subsModal={subsModal}
        showAppForm={showAppForm}
        setShowAppForm={setShowAppForm}
        selectedApplication={selectedApplication}
        setSelectedApplication={setSelectedApplication}
        selectedSubmission={selectedSubmission}
        setSelectedSubmission={setSelectedSubmission}
        setSubsModal={setSubsModal}
        getExplorerUrl={getExplorerUrl}
      />

      {/* ───────── Create / Edit ───────── */}
      <TaskFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initialTask={formInitial}
        project={project}
        onSave={mergeTask}
        innovators={innovators}
        isConnected={isConnected}
      />
    </>
  );
}

export const PERMISSION_TYPES = {
  assign: {
    label: "Assign Someone",
    description: "Creator manually assigns a contributor.",
    badge: "Assign Someone",
    banner:
      "This task can only be done by a contributor chosen by the creator.",
  },
  multiple: {
    label: "Multiple Submissions",
    description: "Anyone can submit work; multiple accepted.",
    badge: "Open to Submissions",
    banner:
      "Anyone can work on this task and submit their work. Multiple submissions are allowed.",
  },
  application: {
    label: "Open to Applications",
    description: "Contributors must apply to be approved.",
    badge: "Open to Applications",
    banner: "The creator will review applications and assign a contributor.",
  },
  claim: {
    label: "Direct Claiming",
    description: "First to claim starts working immediately.",
    badge: "Direct Claiming",
    banner:
      "The first contributor to claim this task can start working immediately.",
  },
};

/* ───────── simple right-side drawer wrapper ───────── */
export function Drawer({ show, onClose, children, isFullscreen = false }) {
  return (
    <>
      {/* backdrop */}
      <div 
        onClick={onClose} 
        className={`fixed inset-0 transition-opacity bg-black/30 z-40 ${show ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} 
      />

      {/* sliding panel */}
      <div
        className={`fixed ${isFullscreen ? 'inset-0' : 'right-0 top-[70px] max-w-4xl max-h-[calc(100vh-70px)]'} 
          h-full w-full overflow-y-auto bg-white shadow-xl transition-all duration-300 z-50
          ${isFullscreen ? 'p-4' : 'p-2'}`}
      >
        <div className="px-2 sm:px-6">{children}</div>
      </div>
    </>
  );
}

export function StatusChip({ status }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-white capitalize ${STATUS_STYLES[status].bg}`}
    >
      <span className={`h-2 w-2 rounded-full ${STATUS_STYLES[status].dot}`} />
      {status.replace("_", " ")}
    </span>
  );
}
